import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [budgetVsActualData, setBudgetVsActualData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [expenditureData, setExpenditureData] = useState(null);

  // Fetch dashboard data
  const fetchDashboard = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.fiscalYear) queryObj.fiscal_year = params.fiscalYear;
        if (params.acadyr) queryObj.acadyr = params.acadyr;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.reportsDashboard}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setDashboardData(response.data);
          return response.data;
        } else {
          message.error(response.msg || "Failed to fetch dashboard data");
          return null;
        }
      } catch (error) {
        message.error("Error fetching dashboard data");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch budget vs actual report
  const fetchBudgetVsActual = useCallback(
    async ({ fiscalYear, term } = {}) => {
      if (!fiscalYear) {
        message.error("Fiscal year is required");
        return null;
      }

      setLoading(true);
      try {
        const query = new URLSearchParams({ fiscal_year: fiscalYear });
        if (term) query.set("term", term);
        const url = `${domainSettings.urls.finance.reportsBudgetVsActual}?${query.toString()}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setBudgetVsActualData(response.data);
          return response.data;
        } else {
          message.error(
            response.msg || "Failed to fetch budget vs actual report",
          );
          return null;
        }
      } catch (error) {
        message.error("Error fetching budget vs actual report");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch revenue report
  const fetchRevenue = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.acadyr) queryObj.acadyr = params.acadyr;
        if (params.dateFrom) queryObj.date_from = params.dateFrom;
        if (params.dateTo) queryObj.date_to = params.dateTo;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.reportsRevenue}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setRevenueData(response.data);
          return response.data;
        } else {
          message.error(response.msg || "Failed to fetch revenue report");
          return null;
        }
      } catch (error) {
        message.error("Error fetching revenue report");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch payroll report
  const fetchPayroll = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.month) queryObj.month = params.month;
        if (params.year && !params.month) queryObj.year = params.year;
        if (params.departmentId) queryObj.department_id = params.departmentId;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.reportsPayroll}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setPayrollData(response.data);
          return response.data;
        } else {
          message.error(response.msg || "Failed to fetch payroll report");
          return null;
        }
      } catch (error) {
        message.error("Error fetching payroll report");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch expenditure report
  const fetchExpenditure = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.fiscalYear) queryObj.fiscal_year = params.fiscalYear;
        if (params.departmentId) queryObj.department_id = params.departmentId;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.reportsExpenditure}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setExpenditureData(response.data);
          return response.data;
        } else {
          message.error(response.msg || "Failed to fetch expenditure report");
          return null;
        }
      } catch (error) {
        message.error("Error fetching expenditure report");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    loading,
    dashboardData,
    budgetVsActualData,
    revenueData,
    payrollData,
    expenditureData,
    fetchDashboard,
    fetchBudgetVsActual,
    fetchRevenue,
    fetchPayroll,
    fetchExpenditure,
  };
};

export default useReports;
